// Carriers list table — FE-SCRM-030.
//
// Columns per spec: name (clickable), carrier_type badge, NAIC,
// contact count, status badge, last_touchpoint_at (relative).

import { Link } from "react-router-dom";

import {
  Badge, Table, TBody, THead, Tr, Th as DsTh, Td as DsTd,
} from "@/design-system";

import {
  CARRIER_STATUS_LABEL,
  CARRIER_TYPE_LABEL,
  formatRelativeTouchpoint,
  isTouchpointCold,
} from "./labels";
import type { CarrierListRow } from "./types";


interface Props {
  rows: CarrierListRow[];
  isRefreshing?: boolean;
}


export function CarriersListTable({ rows, isRefreshing }: Props) {
  return (
    <div
      data-testid="carriers-list-table-wrapper"
      data-refreshing={isRefreshing ? "true" : "false"}
      className={[
        // Subtle in-flight indicator: dim the table during a refetch
        // so filter changes feel responsive without blanking the
        // screen (spec: "show stale data with a subtle loading
        // indicator while refetch happens").
        isRefreshing ? "opacity-70" : "",
      ].filter(Boolean).join(" ")}
    >
      <Table
        dividers
        data-testid="carriers-list-table"
        className="min-w-full divide-y divide-border-subtle"
        wrapperClassName="rounded-md border border-border-subtle"
      >
        <THead className="bg-recessed text-left text-xs uppercase tracking-wide text-text-tertiary">
          <Tr>
            <Th>Carrier</Th>
            <Th>Type</Th>
            <Th>NAIC</Th>
            <Th>Contacts</Th>
            <Th>Status</Th>
            <Th>Last touchpoint</Th>
          </Tr>
        </THead>
        <TBody className="bg-canvas">
          {rows.map((row) => (
            <CarrierRow key={row.carrier_id} row={row} />
          ))}
        </TBody>
      </Table>
    </div>
  );
}


function CarrierRow({ row }: { row: CarrierListRow }) {
  const cold = isTouchpointCold(row.last_touchpoint_at);
  const rel = formatRelativeTouchpoint(row.last_touchpoint_at);
  return (
    <Tr data-testid={`carriers-list-row-${row.carrier_id}`}>
      <Td>
        <Link
          to={`/dashboard/carriers/${encodeURIComponent(row.carrier_id)}`}
          className="font-medium text-text-primary underline-offset-2 hover:underline"
          data-testid={`carriers-list-name-${row.carrier_id}`}
        >
          {row.name}
        </Link>
      </Td>
      <Td>
        <Badge variant="neutral">
          {CARRIER_TYPE_LABEL[row.carrier_type] ?? row.carrier_type}
        </Badge>
      </Td>
      <Td>
        <span className="text-text-secondary">
          {row.naic_code ?? "—"}
        </span>
      </Td>
      <Td>
        <span
          data-testid={`carriers-list-contact-count-${row.carrier_id}`}
          className="text-text-secondary"
        >
          {row.contact_count}
        </span>
      </Td>
      <Td>
        <StatusBadge status={row.status} />
      </Td>
      <Td>
        <span
          data-testid={`carriers-list-last-touchpoint-${row.carrier_id}`}
          className={
            rel == null
              ? "text-text-tertiary"
              : cold
                ? "text-text-tertiary"
                : "text-text-secondary"
          }
        >
          {rel ?? "—"}
        </span>
      </Td>
    </Tr>
  );
}


function StatusBadge({ status }: { status: CarrierListRow["status"] }) {
  // Green-ish ``category`` for active, neutral grey for inactive —
  // mirrors the spec's "green for active, gray for inactive" guidance
  // using the existing Badge variants.
  return (
    <Badge variant={status === "active" ? "category" : "neutral"}>
      {CARRIER_STATUS_LABEL[status] ?? status}
    </Badge>
  );
}


function Th({ children }: { children: React.ReactNode }) {
  return <DsTh>{children}</DsTh>;
}


function Td({ children }: { children: React.ReactNode }) {
  return <DsTd>{children}</DsTd>;
}
